import { useLazyQuery } from '@apollo/client';
import { IncomeLimitArea, IncomeLimitAreaFilter } from '@bybeam/platform-types';
import AutocompleteInput from '@platform-ui-common/components/AutocompleteInput/index.js';
import useSnackbar from '@platform-ui/common/hooks/useSnackbar/index.js';
import IncomeLimitAreaQuery from '@platform-ui/eligibility/graphql/IncomeLimitAreaQuery.graphql';
import { useCallback, useEffect } from 'react';

export interface IncomeLimitAreaAutocompleteProps {
  id: string;
  label: string;
  required?: boolean;
  value: IncomeLimitArea;
  filter?: Partial<IncomeLimitAreaFilter>;
  onChange: (value: IncomeLimitArea) => void;
  error?: string;
}

const getOptionLabel = (option: IncomeLimitArea) =>
  [option?.town, option?.county, option?.state].filter(Boolean).join(', ');

export default function IncomeLimitAreaAutocomplete({
  id,
  label,
  required,
  value,
  filter,
  onChange,
  error: validationError,
}: IncomeLimitAreaAutocompleteProps): JSX.Element {
  const [incomeLimitAreaSearch, { data, error }] = useLazyQuery<{
    incomeLimitAreas: { incomeLimitAreas: IncomeLimitArea[] };
  }>(IncomeLimitAreaQuery);

  const search = useCallback(
    async (input: string) =>
      incomeLimitAreaSearch({ variables: { filter: { search: input, ...filter } } }),
    [incomeLimitAreaSearch, filter],
  );

  const { showSnackbar } = useSnackbar();
  useEffect(() => {
    if (error)
      showSnackbar(
        'An error occurred while searching for income limit areas. Please refresh and try again.',
      );
  }, [error, showSnackbar]);

  return (
    <AutocompleteInput<IncomeLimitArea>
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      search={search}
      options={data?.incomeLimitAreas?.incomeLimitAreas ?? []}
      getOptionLabel={getOptionLabel}
      fullWidth
      required={required}
      error={validationError}
    />
  );
}
